/* Relative overrides are measured against the original cascade, including saved
 * legacy settings, fluid clamp() values, breakpoints and theme-editor CSS.
 * At zero no override is installed. Never transform a fixed element's ancestor.
 */
(() => {
  if (customElements.get('ls-layout-adjustments')) return;
  const instances = new Set();
  let frame = 0;
  const schedule = () => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(update);
  };
  function update() {
    const live = [...instances].filter(node => node.isConnected);
    // Remove ALL our styles before measuring. This prevents feedback between
    // parent text containers and paragraphs, or the header and page sections.
    live.forEach(node => { if (node.sheet) node.sheet.textContent = ''; });
    const mobile = window.matchMedia('(max-width: 989px)').matches;
    const writes = [];
    for (const node of live) {
      const root = document.querySelector(node.dataset.scope);
      if (!root) continue;
      const rules = node.rules || [];
      for (const rule of rules) {
        const delta = Number(mobile ? rule.mobile : rule.desktop);
        if (!Number.isFinite(delta) || delta === 0) continue;
        for (const target of root.querySelectorAll(rule.selector)) {
          const style = getComputedStyle(target);
          const raw = style.getPropertyValue(rule.property);
          let baseline = parseFloat(raw);
          if (rule.property === 'max-width') baseline = parseFloat(style.width) || target.getBoundingClientRect().width;
          if (!Number.isFinite(baseline)) {
            baseline = rule.property === 'max-width' ? target.getBoundingClientRect().width : 0;
          }
          if (raw.endsWith('%') && rule.property !== 'max-width') {
            const parent = target.parentElement;
            baseline = baseline * (parent ? parent.clientWidth : window.innerWidth) / 100;
          }
          const floor = rule.property === 'font-size' ? 8 : 0;
          let value = Math.max(floor, baseline + delta);
          if (rule.property === 'width') value = Math.min(value, target.parentElement.clientWidth);
          if (rule.property === 'column-gap' && style.display === 'grid') {
            const columns = style.gridTemplateColumns.split(' ').length;
            if (columns > 2) {
              const space = target.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
              value = Math.min(value, Math.max(0, space / (columns - 1) * 0.8));
            }
          }
          const index = node.targets.indexOf(target);
          const id = index < 0 ? node.targets.push(target) - 1 : index;
          // Each component owns its attribute; nested scopes cannot collide.
          target.setAttribute(node.attribute, String(id));
          writes.push([node, `:root :is(${node.dataset.scope}):is(${node.dataset.scope}) [${node.attribute}="${id}"] { ${rule.property}: ${value}px !important; }`]);
        }
      }
    }
    for (const [node, css] of writes) {
      if (!node.sheet) {
        node.sheet = document.createElement('style');
        node.sheet.dataset.relativeLayout = '';
        node.append(node.sheet);
      }
      node.sheet.textContent += css;
    }
  }
  let sequence = 0;
  class LayoutAdjustments extends HTMLElement {
    connectedCallback() {
      this.targets = [];
      this.attribute = `data-ls-adjustment-${++sequence}`;
      queueMicrotask(() => {
        if (!this.isConnected) return;
        try { this.rules = JSON.parse(this.querySelector('script[type="application/json"]').textContent).filter(Boolean); }
        catch { this.rules = []; }
        instances.add(this);
        schedule();
      });
    }
    disconnectedCallback() {
      instances.delete(this);
      this.targets?.forEach(target => target.removeAttribute(this.attribute));
      this.sheet?.remove();
      this.sheet = null;
      schedule();
    }
  }
  customElements.define('ls-layout-adjustments', LayoutAdjustments);
  window.addEventListener('resize', schedule, {passive:true});
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);
  document.addEventListener('load', schedule, true);
  document.fonts?.ready.then(schedule);
  document.fonts?.addEventListener('loadingdone', schedule);
})();
